import { useSession } from "@pontimed/auth-client";
import { Navigate, Outlet, createFileRoute, useRouterState } from "@tanstack/react-router";

import { Skeleton } from "@/components/ui/skeleton";

import { hasAdminAccess } from "@/lib/permissions";
import { getPortalUrl } from "@/lib/portal-url";

/**
 * Public layout. Login lives underneath. Authenticated users with admin role
 * bounce to /dashboard; authenticated non-admins are kicked out to portal
 * (the patient/provider SPA) — this admin app is staff-only.
 *
 * Exception: `/2fa-challenge` is reachable while authenticated. The TOTP
 * step-up redirect (admin-context handle403) lands here when /api/admin/*
 * returns TOTP_CHALLENGE_REQUIRED; bouncing the user back to /dashboard
 * before they can complete 2FA produces an infinite loop.
 */
export const Route = createFileRoute("/_public")({
  component: PublicLayout,
});

function PublicLayout() {
  const { status, user } = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isTwoFactorChallenge = pathname === "/2fa-challenge";

  if (status === "loading") {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Skeleton className="h-12 w-48" />
      </div>
    );
  }

  if (status === "authenticated" && !isTwoFactorChallenge) {
    if (hasAdminAccess(user)) {
      return <Navigate to="/dashboard" replace />;
    }
    if (typeof window !== "undefined") {
      window.location.replace(getPortalUrl());
      return null;
    }
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <Outlet />
    </div>
  );
}
